import React from 'react';
import { FormattedMessage } from 'react-intl';
import { createSelector } from 'reselect';

import {
  ADS_HOST,
  CONTENT_TYPE,
  CREATIVE_MODES,
  DISABLE_PERMISSION_CHECKING,
  DISCOVER_HOST,
  EDITOR_MODES,
  EDITOR_SERVICE_MODES,
  MAX_COLOR_SUGGESTION,
  OUTPUT_MEDIA_TYPES,
  PUBLISHING_TARGET,
  REVIEW_AND_FINISH_FLOW_ENABLED,
  WEEK_IN_MILLISECONDS,
} from '../constants';
import { IS_EMBEDDED } from '../features/projects/constants';
import { CREATIVE_GROUPS, MONTH_MESSAGES } from '../features/projects/messages';
import {
  PERMISSION_LEVELS,
  STOCK_ASSET_SEARCH,
} from '../constants/PermissibleActions';
import { CREATIVE_ORIGINS } from '../features/creative/constants';
import { appendQueryParams } from '../util/queryString';
import * as MediaSelectors from './media';
import * as ElementSelectors from './element';
import * as ContentSelectors from './content';
import * as KeyFrameSelectors from './keyframe';
import { getSelectedDisplayElementId } from '../features/editor/shared/state-management/selectors';
import {
  isArray,
  isEmpty as isObjectEmpty,
  isEmptyString,
  isNil,
  isNotNil,
} from '../util/object';
import { isGroup } from '../features/group/util';
import { groupDesignsByStatus } from '../features/templatePicker/filtering/filterUtils';
import * as Log from '../util/log';
import { isEmpty } from '../util/array';
import { getCoordinates, isOverlapping } from '../util/domUtils';
import { isChangedFromTemplate as isLayerChangedFromTemplate } from '../features/shared/layer/util';
import UserContext from '../model/api/UserContext';
import OutputMediaEntityWireObject from '../model/api/wire-objects/OutputMediaEntityWireObject';
import { inEpisodeFlow } from '../features/routes/state-management/selectors';
import {
  EPISODE_CREATOR_ADVERTISER_HELP_URL,
  EPISODE_CREATOR_ADVERTISER_PRODUCT_UPDATES_URL,
  EPISODE_CREATOR_PUBLISHER_HELP_URL,
  EPISODE_CREATOR_PUBLISHER_PRODUCT_UPDATES_URL,
} from '../features/video-reformatting/shot-editor/constants';
import { getTotalFrames } from '../features/shared/playback/state-management/selectors';

const today = new Date();
const thisWeek = new Date(today);
thisWeek.setDate(thisWeek.getDate() - today.getDay()); // Set to Sunday
thisWeek.setHours(0);
thisWeek.setMinutes(0);
thisWeek.setSeconds(0);
thisWeek.setMilliseconds(0);

const thisWeekDiff = +today - +thisWeek;

const lastWeekDiff = thisWeekDiff + WEEK_IN_MILLISECONDS;

const thisMonth = new Date(today);
thisMonth.setDate(0);
thisMonth.setHours(0);
thisMonth.setMinutes(0);
thisMonth.setSeconds(0);
thisMonth.setMilliseconds(0);

const thisMonthDiff = +today - +thisMonth;

const lastMonth = new Date(thisMonth);
lastMonth.setMonth(thisMonth.getMonth() - 1);

const lastMonthDiff = +today - +lastMonth;

// avoid circular dependencies
const getSavedCreativeOrigin = (state) => state.creative.creativeOrigin;
const getIsImportTemplateMode = (state) =>
  isNotNil(state.creative.templateToImport);

export const isUIVisible = (state) => state.playback.uiVisible;
export const getZoomScale = (state) => state.playback.zoomLevel;

export const isTrimmingMedia = (state) => state.mediaTrimmer.isEditing;

export const isSaveRequested = (state) => state.ui.saveRequested;
export const isAutoSaveRequested = (state) => state.ui.autoSaveRequested;
export const isSaving = (state) => state.playback.isSaving;
export const isInPreview = (state) => state.playback.preview;

export const creativeName = (state) => state.playback.creativeName;
export const getCreativeId = (state) => state.playback.creativeId;

export const user = (state) => state.ui.user;
export const getUserPermissions = (state) => state.ui.permissions;
export const getWildcardOrgPermissions = (state) =>
  state.ui.wildcardOrgPermissions;
export const getWildcardPubPermissions = (state) =>
  state.ui.wildcardPubPermissions;
export const getWildcardSnapProPermissions = (state) =>
  state.ui.wildcardSnapProPermissions;
export const getGlobalPermissions = (state) => state.ui.globalPermissions;

export const isBasicMode = (state) => state.ui.editModeBasic;
export const isEditingTemplateMode = (state) => state.ui.editModeTemplate;
export const editTemplate = (state) => state.ui.editTemplate;
export const editTemplateLoading = (state) => state.ui.editTemplateLoading;
export const editTemplateOriginalTags = (state) =>
  state.ui.editTemplateOriginalTags;
export const isGlobalTemplate = (state) => state.ui.editTemplateIsGlobal;
export const template = (state) => state.ui.template;
export const templateList = (state) => state.ui.templateList;
export const templateListLoading = (state) => state.ui.templateListLoading;
export const globalTemplateList = (state) => state.ui.globalTemplateList;
export const globalTemplateListLoading = (state) =>
  state.ui.globalTemplateListLoading;
export const creativeList = (state) => state.ui.creativeList;
export const creativeListLoading = (state) => state.ui.creativeListLoading;
export const mediaList = (state) => state.ui.mediaList;
export const mediaListLoading = (state) => state.ui.mediaListLoading;
export const pageLoading = (state) => state.ui.pageLoading;
export const pageError = (state) => state.ui.pageError;
export const creativeLoading = (state) => state.ui.creativeLoading;
export const getOutputMediaId = (state) => state.ui.outputMediaId;
export const getOutputMediaEntities = (state) => state.ui.outputMediaEntities;
export const elementContextVisible = (state) => state.ui.elementContextVisible;
export const areToolsVisible = (state) => state.ui.areToolsVisible;
export const templateDataModal = (state) => state.ui.templateDataModal;
export const mediaGalleryOpen = (state) => state.mediaGallery.isVisible;
export const toDelete = (state) => state.ui.toDelete;
export const organizationList = (state) => state.ui.organizationList;
export const currentOrganization = (state) => state.ui.organization;
export const currentOrganizationPermissions = (state) =>
  state.ui.organizationPermissions;
export const currentPublisher = (state) => state.ui.publisher;
export const currentPublisherPermissions = (state) =>
  state.ui.publisherPermissions;
export const currentSnapProProfile = (state) => state.ui.snapProProfile;
export const currentSnapProProfilePermissions = (state) =>
  state.ui.snapProProfilePermissions;
export const accountList = (state) => state.ui.accountList;
export const getCurrentAccount = (state) => state.ui.account;
export const currentAccountPermissions = (state) => state.ui.accountPermissions;
export const isAccessForbidden = (state) => state.ui.accessForbidden;
export const getIsShowingBuildProgressModal = (state) =>
  state.ui.isShowingBuildProgressModal;
export const getIsShowingMediaUploadProgressModal = (state) =>
  state.ui.isShowingMediaUploadProgressModal;
export const willAutoSaveSoon = (state) => state.ui.willAutoSaveSoon;
export const shouldWarnAboutTemplateMedia = (state) =>
  state.ui.shouldWarnAboutTemplateMedia;
export const getAuthLocation = (state) => state.ui.authLocation;
export const getPlainPublisherList = (state) => state.ui.publisherList;
export const getPlainSnapProProfilesList = (state) =>
  state.ui.snapProProfilesList;
export const isMembershipSwitchModalOpen = (state) =>
  state.ui.showMembershipSwitchModal;
export const getRequestedPublisherId = (state) => state.ui.requestedPublisher;
export const getRequestedAccountId = (state) => state.ui.requestedAccount;
export const getErrorModal = (state) => state.ui.errorModal;
export const isErrorModalVisible = (state) => state.ui.errorModal.isVisible;
export const getHoveredGroupElement = (state) => state.ui.hoveredGroupElement;

export const getCurrentEdition = (state) => state.ui.edition;
export const getCurrentSnapId = (state) => state.ui.snapId;
export const getCurrentPublisher = (state) => state.ui.publisher;
export const getCurrentSnapProProfile = (state) => state.ui.snapProProfile;
export const isDraggingTimeline = (state) => state.ui.isDraggingTimeline;
export const isExpandingElement = (state) => state.ui.isExpandingElement;
export const isExpandingElementId = (state) =>
  state.ui.isExpandingElement ? state.ui.isExpandingElementId : null;

export const getAlphaCompositeRulePreview = (state) =>
  state.ui.alphaCompositeRulePreview;

export const getIsMouseDown = (state) => state.ui.isMouseDown;
export const getCurrentColor = (state) => state.ui.currentColor;
export const getMostRecentColors = (state) => state.ui.mostRecentColors;

export const getEditorMode = (state) => state.playback.editorMode;
export const getEditorServiceMode = (state) => state.playback.editorServiceMode;

export const isPhotoEditMode = (state) =>
  getEditorMode(state) === EDITOR_MODES.PHOTO;
export const isSelfServiceMode = (state) =>
  getEditorServiceMode(state) === EDITOR_SERVICE_MODES.SELF;

// To avoid a circular dependency issue
const getAllIds = (state) => {
  return ElementSelectors.getAllIds(state);
};

// To avoid a circular dependency issue
const getRootIds = (state) => {
  return ElementSelectors.getRootIds(state);
};

// To avoid a circular dependency issue
const getDepthFromRoot = (state) => {
  return ElementSelectors.getDepthFromRoot(state);
};

// To avoid a circular dependency issue
const getLayerById = (state, layerId) => {
  return ElementSelectors.getLayerById(state, layerId);
};

// To avoid a circular dependency issue
const getAllElements = (state) => {
  return ElementSelectors.getAllElements(state);
};

// To avoid a circular dependency issue
const getAllElementsById = (state) => {
  return ElementSelectors.getAllElementsById(state);
};

// To avoid a circular dependency issue
const getAllElementsReversed = (state) => {
  return ElementSelectors.getAllElementsReversed(state);
};

// To avoid a circular dependency issue
const getAllLayers = (state) => {
  return ElementSelectors.getAllLayers(state);
};

// To avoid a circular dependency issue
const getAllContentById = (state) => {
  return ContentSelectors.getAllContentById(state);
};

// To avoid a circular dependency issue
const getAllMediaById = (state) => {
  return MediaSelectors.getAllMediaById(state);
};

export const isNewElementRequested = (state) => {
  return (
    Boolean(state.ui.newElementRequested) &&
    !creativeLoading(state) &&
    !editTemplateLoading(state)
  );
};

export const getNewElementContentType = (state) =>
  isNewElementRequested(state) && state.ui.newElementContentType;
export const getSelectedMediaTab = (state) => state.ui.selectedMediaTab;

export const publisherList = createSelector(
  [getPlainPublisherList, getCurrentPublisher],
  (list, currentPublisher) => {
    if (!list) {
      return null;
    }
    return list.map((publisher) => {
      return {
        id: publisher.id,
        name: publisher.name,
        hostUsername: publisher.hostUsername,
        isSelected: currentPublisher && currentPublisher.id === publisher,
      };
    });
  },
);

export const snapProProfilesList = createSelector(
  [getPlainSnapProProfilesList, getCurrentSnapProProfile],
  (list, currentSnapProProfile) => {
    if (!list) {
      return null;
    }
    return list.map((snapProProfile) => {
      return {
        id: snapProProfile.id,
        name: snapProProfile.title,
        isSelected:
          currentSnapProProfile && currentSnapProProfile.id === snapProProfile,
      };
    });
  },
);

export const getBuildProgressPercent = (state) => state.ui.buildProgressPercent;
export const getPublishProgressPercent = (state) =>
  state.ui.publishProgressPercent;
export const lastBuiltCreativeId = (state) => state.ui.lastBuiltCreativeId;
export const lastBuiltCreativeVersionId = (state) =>
  state.ui.lastBuiltCreativeVersionId;
export const lastPublishedAssetId = (state) => state.ui.lastPublishedAssetId;
export const lastPublishedAdsAPIMediaId = (state) =>
  state.ui.lastPublishedAdsAPIMediaId;

export const canvasOffset = (state) => state.ui.canvasOffset;
export const isSavingAndBuilding = (state) => state.ui.isSavingAndBuilding;
export const isPublishing = (state) => state.ui.isPublishing;
export const isSavingTemplate = (state) => state.ui.isSavingTemplate;
export const isBuildComplete = (state) =>
  !state.ui.isBuilding && state.ui.buildProgressPercent === 1;
export const isPublishComplete = (state) =>
  !state.ui.isPublishing && state.ui.publishProgressPercent === 1;

export const getPublishingTarget = createSelector(
  [getCurrentAccount, getCurrentPublisher],
  (account, publisher) => {
    if (isNotNil(account)) {
      return PUBLISHING_TARGET.ADS;
    } else if (isNotNil(publisher)) {
      return PUBLISHING_TARGET.DISCOVER;
    }

    return PUBLISHING_TARGET.NONE;
  },
);

export const getCreativeTemplateMedia = (state, mediaId) => {
  const templateObj = template(state);

  if (!templateObj) {
    return null;
  }

  return (
    templateObj.media &&
    templateObj.media.mediaById &&
    templateObj.media.mediaById[mediaId]
  );
};

export const getCreativeTemplateContent = (state, contentId) => {
  const templateObj = template(state);

  if (!templateObj) {
    return null;
  }

  return (
    templateObj.content &&
    templateObj.content.contentById &&
    templateObj.content.contentById[contentId]
  );
};

export const getCreativeTemplateElement = (state, elementId) => {
  const templateObj = template(state);

  if (!templateObj) {
    return null;
  }

  return (
    templateObj.elements &&
    templateObj.elements.elementsById &&
    templateObj.elements.elementsById[elementId]
  );
};

const isChangedFromTemplateHelper = (
  layer,
  currentTemplate,
  editTemplate,
  templateElement,
  isImportTemplateMode,
) => {
  if (isNil(layer)) return true;
  const isEditTemplate = Boolean(editTemplate) && !Boolean(currentTemplate);
  const overwrite = isEditTemplate && !isImportTemplateMode;

  if (overwrite) return true;
  if (layer.excludedFromTemplate) return true;

  return isLayerChangedFromTemplate({
    content: layer.content,
    currentTemplate,
    medium: layer.medium,
    templateElement,
  });
};

export const isChangedFromTemplate = createSelector(
  [
    getLayerById,
    template,
    editTemplate,
    getCreativeTemplateElement,
    getIsImportTemplateMode,
  ],
  (
    layer,
    currentTemplate,
    editTemplate,
    templateElement,
    isImportTemplateMode,
  ) => {
    /* Should only be set when editing a template, in which case we don't need to do this check */
    return isChangedFromTemplateHelper(
      layer,
      currentTemplate,
      editTemplate,
      templateElement,
      isImportTemplateMode,
    );
  },
);

/**
 * Gets a list of creatives grouped with a label
 * @param list
 * @param user
 * @returns {[]|*}
 */
const groupCreativeList = (list, user) => {
  if (!list) {
    return list;
  }

  // Filter list by user
  // TODO allow user to change this filter
  list = list.filter((creative) => {
    // In Snap SSO, the user ID is saved. In Google Auth, it is the user email address.
    // We support both auth methods, so we have to check against both fields.
    return creative.createdBy === user.id || creative.createdBy === user.email;
  });

  list.sort((first, second) => {
    const firstDate = new Date(first.updatedAt);
    const secondDate = new Date(second.updatedAt);

    // Reverse chronological order until we implement sorting/filtering
    return secondDate.getTime() - firstDate.getTime();
  });

  const groups = {};

  const now = Date.now();
  list.forEach((creative) => {
    const creativeGroupWrapper = {
      creative,
    };

    const { updatedAt } = creative;
    const creativeDate = new Date(updatedAt);
    const timeDiff = now - +creativeDate;

    if (timeDiff < thisWeekDiff) {
      creativeGroupWrapper.group = CREATIVE_GROUPS.THIS_WEEK;
    } else if (timeDiff < lastWeekDiff) {
      creativeGroupWrapper.group = CREATIVE_GROUPS.LAST_WEEK;
    } else if (timeDiff < thisMonthDiff) {
      creativeGroupWrapper.group = CREATIVE_GROUPS.THIS_MONTH;
    } else if (timeDiff < lastMonthDiff) {
      creativeGroupWrapper.group = CREATIVE_GROUPS.LAST_MONTH;
    } else {
      const creativeStartOfMonth = new Date(creativeDate);
      creativeStartOfMonth.setDate(0);
      creativeStartOfMonth.setHours(0);
      creativeStartOfMonth.setMinutes(0);
      creativeStartOfMonth.setSeconds(0);
      creativeStartOfMonth.setMilliseconds(0);
      const groupKey =
        CREATIVE_GROUPS.LAST_MONTH.key + (+thisMonth - +creativeStartOfMonth);
      let label = MONTH_MESSAGES[creativeDate.getMonth()];

      if (creativeDate.getFullYear() !== today.getFullYear()) {
        label = (
          <FormattedMessage
            defaultMessage='{month} {year}'
            description='Month Year Label'
            values={{
              month: MONTH_MESSAGES[creativeDate.getMonth()],
              year: creativeDate.getFullYear(),
            }}
          />
        );
      }

      creativeGroupWrapper.group = {
        key: groupKey,
        label,
        creative,
      };
    }

    const {
      group: { key, label },
      creative: wrappedCreative,
    } = creativeGroupWrapper;
    if (!groups[key]) {
      groups[key] = {
        key,
        label,
        creatives: [],
      };
    }

    groups[key].creatives.push(wrappedCreative);
  });

  const groupKeys = Object.keys(groups).sort((first, second) => {
    return first - second;
  });
  const groupedCreatives = [];
  groupKeys.forEach((key) => {
    groupedCreatives.push(groups[key]);
  });

  return groupedCreatives;
};

export const getEpisodeList = createSelector(
  [creativeList, user],
  (list, user) => {
    if (!list) {
      return list;
    }

    const filteredList = list.filter((item) => {
      return item.creativeMode === CREATIVE_MODES.SHOWS;
    });
    return groupCreativeList(filteredList, user);
  },
);

export const getCreativeListModeFilter = (state) => {
  return state.ui.creativeListModeFilter;
};

export const getFullProjectList = createSelector(
  [creativeList, user],
  (fullList, user) => {
    if (isNil(fullList)) {
      return fullList;
    }

    fullList = fullList.filter((creative) => {
      // In Snap SSO, the user ID is saved. In Google Auth, it is the user email address.
      // We support both auth methods, so we have to check against both fields.
      return (
        creative.createdBy === user.id || creative.createdBy === user.email
      );
    });

    // If embedded mode, filter out shows for now.
    // TODO(lemieux) circle back to this and refactor
    if (IS_EMBEDDED) {
      fullList = fullList.filter((item) => {
        return item.creativeMode !== CREATIVE_MODES.SHOWS;
      });
    }

    return fullList;
  },
);

export const getProjectCount = createSelector(
  [getFullProjectList],
  (fullList) => {
    if (isNil(fullList) || isEmpty(fullList)) return 0;

    return fullList.length;
  },
);

export const getGroupedFilteredProjectList = createSelector(
  [getFullProjectList, user, getCreativeListModeFilter],
  (fullList, user, modeFilter) => {
    if (isNil(fullList)) {
      return fullList;
    }

    if (isEmptyString(modeFilter)) return groupCreativeList(fullList, user);

    const filteredList = fullList.filter((item) => {
      return item.creativeMode === modeFilter;
    });

    return groupCreativeList(filteredList, user);
  },
);

export const getCreativeList = createSelector(
  [creativeList, user],
  (list, user) => {
    if (!list) {
      return list;
    }

    const filteredList = list.filter((item) => {
      return item.creativeMode !== CREATIVE_MODES.SHOWS;
    });
    return groupCreativeList(filteredList, user);
  },
);

export const groupedTemplateList = createSelector([templateList], (list) => {
  return groupDesignsByStatus(list);
});

const hasPermission = (permittee, permissionsList, actionType) => {
  if (DISABLE_PERMISSION_CHECKING) {
    return true;
  }

  // TODO based on list of allowed actions in permissionsList return if request action is allowed
  const { name } = actionType;
  if (!name) {
    Log.error('Permissible Action did not have a name', actionType);
    return false;
  }
  return permittee && permissionsList && permissionsList.includes(name);
};

const getAllPermissionsAndPermitees = createSelector(
  [
    user,
    getCurrentAccount,
    currentOrganization,
    getCurrentPublisher,
    getCurrentSnapProProfile,
    getUserPermissions,
    getWildcardOrgPermissions,
    getWildcardPubPermissions,
    getWildcardSnapProPermissions,
    getGlobalPermissions,
    currentOrganizationPermissions,
    currentAccountPermissions,
    currentPublisherPermissions,
    currentSnapProProfilePermissions,
  ],
  (
    user,
    account,
    organization,
    publisher,
    snapProProfile,
    userPermissions,
    wildcardOrgPermissions,
    wildcardPubPermissions,
    wildcardSnapProPermissions,
    globalPermissions,
    organizationPermissions,
    accountPermissions,
    publisherPermissions,
    snapProProfilePermissions,
  ) => {
    return {
      user,
      userPermissions,
      wildcardOrgPermissions,
      wildcardPubPermissions,
      wildcardSnapProPermissions,
      globalPermissions,
      account,
      accountPermissions,
      organization,
      organizationPermissions,
      publisher,
      publisherPermissions,
      snapProProfile,
      snapProProfilePermissions,
    };
  },
);

const actionTypePassThrough = (state, actionType) => {
  return actionType;
};

export const hasPermissionForAction = createSelector(
  [getAllPermissionsAndPermitees, actionTypePassThrough],
  (
    {
      user,
      account,
      organization,
      publisher,
      snapProProfile,
      userPermissions,
      wildcardOrgPermissions,
      wildcardPubPermissions,
      wildcardSnapProPermissions,
      globalPermissions,
      organizationPermissions,
      accountPermissions,
      publisherPermissions,
      snapProProfilePermissions,
    },
    actionType,
  ) => {
    // Permissions are acquired by requesting your permission for each entity for which you are affiliated.
    // The only 'hierarchy' here is within Ad Organization and Ad Account. If you have permission in
    // the org you also have it in the account and the API should be rolling those up in to one list.
    // Since we may only know about your organization at this point, we have to check each separately.
    // 'Global Organization'  permissions grant your permission to make changes in the Global context but SHOULD NOT
    // supersede other permissions. Global permissions are give a specific magic 'GLOBAL' org or publisher.
    // When switching between orgs and publishers we should re-fetch those permissions specifically.
    // 'Wildcard' permissions ('*') are loaded in the context of publisher or organization and are merged with those sets on load.

    // FIXME: in the future, this will need to be decoupled from the action,
    // unless we want to make UI specific action types.
    // Which could also work.
    const { requiredLevel } = actionType;

    switch (requiredLevel) {
      case PERMISSION_LEVELS.GLOBAL_ORG:
        return hasPermission(user, globalPermissions, actionType);
      case PERMISSION_LEVELS.NO_CONTEXT /* Previously mislabeled 'USER' */:
        return hasPermission(user, userPermissions, actionType);
      case PERMISSION_LEVELS.GROUP:
        return (
          hasPermission(organization, organizationPermissions, actionType) ||
          hasPermission(organization, wildcardOrgPermissions, actionType) ||
          hasPermission(publisher, publisherPermissions, actionType) ||
          hasPermission(publisher, wildcardPubPermissions, actionType) ||
          hasPermission(
            snapProProfile,
            wildcardSnapProPermissions,
            actionType,
          ) ||
          hasPermission(snapProProfile, snapProProfilePermissions, actionType)
        );
      case PERMISSION_LEVELS.ORGANIZATION:
        return hasPermission(organization, organizationPermissions, actionType);
      case PERMISSION_LEVELS.PUBLISHER:
        return hasPermission(publisher, publisherPermissions, actionType);
      case PERMISSION_LEVELS.SNAP_PRO:
        return hasPermission(
          snapProProfile,
          snapProProfilePermissions,
          actionType,
        );
      case PERMISSION_LEVELS.AD_ACCOUNT:
        return hasPermission(account, accountPermissions, actionType);
      case PERMISSION_LEVELS.OWNED:
        return (
          // TODO wildcard permission on org/publisher
          /* Ad Accounts */ hasPermission(
            account,
            accountPermissions,
            actionType,
          ) ||
          /* Ad Organization */ hasPermission(
            organization,
            organizationPermissions,
            actionType,
          ) ||
          /* All Organizations */ hasPermission(
            organization,
            wildcardOrgPermissions,
            actionType,
          ) ||
          /* Publisher  */ hasPermission(
            publisher,
            publisherPermissions,
            actionType,
          ) ||
          /* All Publishers */ hasPermission(
            publisher,
            wildcardPubPermissions,
            actionType,
          ) ||
          /* Snap Pro Profile */ hasPermission(
            snapProProfile,
            snapProProfilePermissions,
            actionType,
          ) ||
          /* All Snap Pro Profiles */ hasPermission(
            snapProProfile,
            wildcardSnapProPermissions,
            actionType,
          )
        );
      default:
        return false;
    }
  },
);

/*
 Gets a media url and if from a template, appends the template ID in the query string
 */
export const getMediaUrl = (state, mediaId) => {
  const mediaBaseUrl = MediaSelectors.getUrlById(state, mediaId);
  if (isNil(mediaBaseUrl)) return mediaBaseUrl;

  const templateMedia = getCreativeTemplateMedia(state, mediaId);

  if (templateMedia) {
    const currentTemplate = template(state);
    return appendQueryParams(mediaBaseUrl, {
      templateId: currentTemplate.id,
    });
  }

  const media = MediaSelectors.getById(state, mediaId);
  if (media && media.templateId) {
    return appendQueryParams(mediaBaseUrl, {
      templateId: media.templateId,
    });
  }

  return mediaBaseUrl;
};

/**
 * Get the current user context based on state.
 *
 * @type {Function}
 * @param {object} state
 * @return {UserContext} userContext
 */
export const getCurrentUserContext = createSelector(
  [
    getCurrentAccount,
    currentOrganization,
    getCurrentPublisher,
    getCurrentSnapProProfile,
    getCurrentEdition,
  ],
  (
    currentAccount,
    currentOrganization,
    currentPublisher,
    snapProProfile,
    currentEdition,
  ) => {
    const userContext = new UserContext();

    if (isNotNil(currentAccount)) {
      userContext.adAccountId = currentAccount.id;
    }

    if (isNotNil(currentOrganization)) {
      userContext.organizationId = currentOrganization.id;
    }

    if (isNotNil(currentPublisher)) {
      userContext.publisherId = currentPublisher.id;
    }

    if (isNotNil(snapProProfile)) {
      userContext.businessProfileId = snapProProfile.id;
    }

    if (isNotNil(currentEdition)) {
      userContext.editionId = currentEdition.id;
    }

    return userContext;
  },
);

export const isAdvertiser = (state) => {
  const context = getCurrentUserContext(state);
  return (
    context && isNotNil(context.organizationId) && isNotNil(context.adAccountId)
  );
};

export const isPublisher = (state) => {
  const context = getCurrentUserContext(state);
  return context && isNotNil(context.publisherId);
};

const getCreativeBuilderPath = (accountId, assetId, mediaId) => {
  const encodedAssetId = encodeURIComponent(assetId);
  const encodedMediaId = encodeURIComponent(mediaId);
  const assetIdParams = `&__assetId=${encodedAssetId}&__mediaId=${encodedMediaId}`;
  return `${accountId}/creatives/builder/?adProduct=SNAP_AD&__creativeType=SNAP_AD${assetIdParams}`;
};

export function getPublishedSnapAdsUrl(state) {
  const host = ADS_HOST;
  const account = getCurrentAccount(state);
  const assetId = lastPublishedAssetId(state) || '';
  const mediaId = lastPublishedAdsAPIMediaId(state) || '';
  const accountId = account && account.id;

  let path;
  if (accountId) {
    path = getCreativeBuilderPath(accountId, assetId, mediaId);
  } else {
    path = '';
  }

  return `https://${host}/${path}`;
}

export function getPublishedSnapDiscoverUrl(state) {
  const host = DISCOVER_HOST;
  const publisher = getCurrentPublisher(state);
  const publisherId = publisher && publisher.id;

  let path;
  if (publisherId) {
    path = `v2/publisher/${publisherId}/media`;
  } else {
    path = '';
  }

  return `https://${host}/${path}`;
}

export const permissionsAreLoaded = createSelector(
  [
    getCurrentUserContext,
    user,
    getUserPermissions,
    getWildcardOrgPermissions,
    getWildcardPubPermissions,
    getWildcardSnapProPermissions,
    getGlobalPermissions,
    currentAccountPermissions,
    currentOrganizationPermissions,
    currentPublisherPermissions,
    currentSnapProProfilePermissions,
  ],
  (
    userContext,
    user,
    userPermissions,
    wildcardOrgPermissions,
    wildcardPubPermissions,
    wildcardSnapProPermissions,
    globalPermissions,
    accountPermissions,
    orgPermissions,
    publisherPermissions,
    snapProProfilePermissions,
  ) => {
    if (!user || !isArray(userPermissions) || !isArray(globalPermissions)) {
      return false;
    }

    const {
      publisherId,
      organizationId,
      adAccountId,
      businessProfileId,
    } = userContext;
    if (isNotNil(publisherId)) {
      return isArray(publisherPermissions) && isArray(wildcardPubPermissions);
    }

    if (isNotNil(organizationId) && isNotNil(adAccountId)) {
      return (
        isArray(orgPermissions) &&
        isArray(accountPermissions) &&
        isArray(wildcardOrgPermissions)
      );
    }

    if (isNotNil(businessProfileId)) {
      return (
        isArray(snapProProfilePermissions) &&
        isArray(wildcardSnapProPermissions)
      );
    }

    return false;
  },
);

export const getColorRecommendations = createSelector(
  [
    getAllContentById,
    getAllMediaById,
    getSelectedDisplayElementId,
    getAllElementsReversed,
    (state) => state,
  ],
  (content, media, selectedElementId, allElements, state) => {
    if (!selectedElementId) return null;

    const elementLayerIndex = allElements.findIndex(
      (element) => element.elementId === selectedElementId,
    );
    if (elementLayerIndex === -1) return null;

    const currentElement = allElements[elementLayerIndex];
    const layersBehindCurrentElement = [...allElements].splice(
      elementLayerIndex + 1,
    );

    // get coordinates of current layer
    const currentTransform = KeyFrameSelectors.getTransformForCurrentFrame(
      state,
      selectedElementId,
    );
    const height = ContentSelectors.getHeight(state, currentElement.contentId);
    const width = ContentSelectors.getWidth(state, currentElement.contentId);
    const { rotation, scale, translation } = currentTransform;
    const cornerCoordinates = getCoordinates(
      translation,
      height,
      width,
      scale.x,
      scale.y,
      rotation,
    );

    const contentFound = layersBehindCurrentElement.find((element) => {
      const { contentId, elementId } = element;
      const contentType = content[contentId].contentType;
      if (
        !(
          contentType === CONTENT_TYPE.IMAGE || contentType === CONTENT_TYPE.SVG
        )
      ) {
        return false;
      }

      const currentTransform = KeyFrameSelectors.getTransformForCurrentFrame(
        state,
        elementId,
      );

      // determine if two layers overlap
      const layerHeight = ContentSelectors.getHeight(state, contentId);
      const layerWidth = ContentSelectors.getWidth(state, contentId);
      const {
        rotation: layerRotation,
        scale: layerScale,
        translation: layerTranslation,
      } = currentTransform;
      const layerCornerCoordinates = getCoordinates(
        layerTranslation,
        layerHeight,
        layerWidth,
        layerScale.x,
        layerScale.y,
        layerRotation,
      );

      return isOverlapping(cornerCoordinates, layerCornerCoordinates);
    });

    // none of the layers behind are images
    if (!contentFound) return null;

    const { contentId } = contentFound;
    const { mediaId } = content[contentId];
    const { colors: currentColors } = media[mediaId];
    if (isNil(currentColors) || isObjectEmpty(currentColors.colors)) {
      return null;
    }

    const colors = currentColors.colors.slice(0, MAX_COLOR_SUGGESTION);
    const complements = currentColors.complementary.slice(
      0,
      MAX_COLOR_SUGGESTION,
    );

    return [...colors, ...complements];
  },
);

export const getFilteredCreativeElements = createSelector(
  [
    getAllIds,
    getAllElements,
    getAllContentById,
    isEditingTemplateMode,
    getTotalFrames,
  ],
  (elementIds, elements, contentById, templateMode, totalFrames) => {
    const filteredElementIds = elementIds.filter((id, index) => {
      const element = elements[index];
      if (isNil(element)) return false;

      const { contentId, frameStart } = element;

      const content = contentById[contentId];
      if (isNotNil(content) && isGroup(content.contentType)) return false;

      const excludeFromTemplate =
        !templateMode &&
        element &&
        element.meta &&
        element.meta.excludeFromTemplate;
      if (excludeFromTemplate) return false;

      return frameStart < totalFrames;
    });

    return filteredElementIds;
  },
);

const getSetOfElementIdsThatStillHaveTemplateMedia = createSelector(
  [getAllLayers, template, editTemplate, getIsImportTemplateMode],
  (layers, currentTemplate, editTemplate, isImportTemplateMode) => {
    const statusSet = new Set();

    if (!isEmpty(layers)) {
      layers.forEach((layer) => {
        const { originalLayer: templateElement } = layer;
        const isChanged = isChangedFromTemplateHelper(
          layer,
          currentTemplate,
          editTemplate,
          templateElement,
          isImportTemplateMode,
        );
        if (!isChanged) {
          statusSet.add(layer.id);
        }
      });
    }

    return statusSet;
  },
);

// Get elements that need media to be replaced (via Template)
// FIXME(lemieux) might be worth using reselect for this
export const getElementsThatNeedNewMedia = createSelector(
  [getAllElements, getSetOfElementIdsThatStillHaveTemplateMedia],
  (elements, elementsThatHaveTemplateMedia) => {
    if (!elements) {
      return null;
    }

    const filteredElements = elements.filter(({ elementId }) => {
      return elementsThatHaveTemplateMedia.has(elementId);
    });

    if (isEmpty(filteredElements)) return null;
    return filteredElements;
  },
);

export const getPublisherById = createSelector(
  [publisherList, (state, publisherId) => publisherId],
  (publisherList, publisherId) => {
    return (
      isArray(publisherList) &&
      publisherList.find((publisher) => publisher.id === publisherId)
    );
  },
);

export const getAccountById = createSelector(
  [accountList, (state, accountId) => accountId],
  (accountList, accountId) => {
    return (
      isArray(accountList) &&
      accountList.find((account) => account.id === accountId)
    );
  },
);

export const getOrganizationById = createSelector(
  [organizationList, (state, organizationId) => organizationId],
  (organizationList, organizationId) => {
    return (
      isArray(organizationList) &&
      organizationList.find(
        (organization) => organization.id === organizationId,
      )
    );
  },
);

export const getOrganizationAccountList = createSelector(
  [accountList, (state, organizationId) => organizationId],
  (accountList, organizationId) => {
    if (isEmpty(accountList)) {
      return null;
    }

    return accountList.filter(
      ({ organization_id: accountOrganizationId }) =>
        accountOrganizationId === organizationId,
    );
  },
);

/**
 * Whether all template elements have been replaced with new media.
 * If this is false, the user will not be allowed to publish.
 */
export const allTemplateMediaReplaced = createSelector(
  [
    getAllIds,
    getAllElementsById,
    getAllContentById,
    getSetOfElementIdsThatStillHaveTemplateMedia,
  ],
  (elementIds, elementsById, contentById, elementsThatHaveTemplateMedia) => {
    return elementIds
      .map((elementId) => {
        const element = elementsById[elementId];
        const { contentId } = element;
        const content = contentById[contentId];
        return {
          element,
          content,
        };
      })
      .filter(({ content }) => {
        if (isNil(content)) return false;
        const { contentType } = content;
        switch (contentType) {
          case CONTENT_TYPE.ELEMENT_EFFECT_GROUP:
          case CONTENT_TYPE.ELEMENT_GROUP:
          case CONTENT_TYPE.SHAPE:
          case CONTENT_TYPE.TEXT:
            return false;
          default:
            return true;
        }
      })
      .every(
        ({ element: { elementId } }) =>
          !elementsThatHaveTemplateMedia.has(elementId),
      );
  },
);

export const getUserPreferenceHelper = (user, preferenceName) => {
  if (!user || isNil(preferenceName)) {
    return null;
  }

  const { preferences } = user;
  if (!preferences) {
    return null;
  }

  const { [preferenceName]: value } = preferences;
  return value;
};

export const getUserPreference = createSelector(
  [user, (state, preferenceName) => preferenceName],
  (user, preferenceName) => getUserPreferenceHelper(user, preferenceName),
);

export const isUserAnAdvertiser = (state) =>
  getPublishingTarget(state) === PUBLISHING_TARGET.ADS;
export const isUserAPublisher = (state) =>
  getPublishingTarget(state) === PUBLISHING_TARGET.DISCOVER;

export const isReviewAndFinishFlowAvailable = (state) =>
  REVIEW_AND_FINISH_FLOW_ENABLED && !isEditingTemplateMode(state);

export const isReferredFromPublishingTarget = (state) => {
  const publishingTargetHost =
    getPublishingTarget(state) === PUBLISHING_TARGET.ADS
      ? ADS_HOST
      : DISCOVER_HOST;
  return (
    document.referrer &&
    document.referrer.startsWith(`https://${publishingTargetHost}`)
  );
};

// @todo(chuti) - this should be in features/creative but due to circular dep, leave it here for now
export const getCreativeOrigin = createSelector(
  [
    getSavedCreativeOrigin,
    isReferredFromPublishingTarget,
    isUserAnAdvertiser,
    isUserAPublisher,
  ],
  (
    savedCreativeOrigin,
    referredFromPublishingTarget,
    isAdvertiser,
    isPublisher,
  ) => {
    if (isNotNil(savedCreativeOrigin)) {
      return savedCreativeOrigin;
    }

    if (!IS_EMBEDDED) {
      return CREATIVE_ORIGINS.DIRECT;
    }

    if (referredFromPublishingTarget) {
      if (isAdvertiser) {
        return CREATIVE_ORIGINS.AD_MANAGER_EMBED;
      }

      if (isPublisher) {
        return CREATIVE_ORIGINS.STORY_STUDIO;
      }
    }

    return null;
  },
);

export const getCurrentAccountName = (state) => {
  if (isUserAnAdvertiser(state)) {
    const { name } = getCurrentAccount(state);
    return name;
  } else {
    const { name } = getCurrentPublisher(state);
    return name;
  }
};

export const isUserInMultipleAccounts = createSelector(
  [accountList, getPlainPublisherList],
  (accountList, publisherList) => {
    const totalAccounts = accountList ? accountList.length : 0;
    const totalPublishers = publisherList ? publisherList.length : 0;
    return totalAccounts + totalPublishers > 1;
  },
);

export const isDraggingTimelineAllowed = createSelector(
  [getRootIds, getDepthFromRoot],
  (rootElements, depth) => {
    return rootElements.length > 1 || depth > 1;
  },
);

export const getGroupName = (state) => {
  const organization = currentOrganization(state);
  const publisher = getCurrentPublisher(state);
  return (organization && organization.name) || (publisher && publisher.name);
};

/*
 * Publisher accounts are currently not allowed to use stock media search, otherwise we check
 * for permission at the organization level, but if any org you belong to has it you get it as a user
 * and the value will not change from org to org.
 */
export const hasStockMediaSearch = (state) =>
  !isPublisher(state) && hasPermissionForAction(state, STOCK_ASSET_SEARCH);

export const getOutputMediaEntitiesForEpisode = createSelector(
  [getOutputMediaEntities],
  (outputMediaEntities) => {
    if (isNil(outputMediaEntities)) return undefined;

    const filteredEntities = [];
    outputMediaEntities.forEach((itemJson) => {
      const outputMediaEntity = OutputMediaEntityWireObject.fromJSON(itemJson);
      if (
        outputMediaEntity.outputMediaType ===
        OUTPUT_MEDIA_TYPES.PARTIAL_CREATIVE
      ) {
        filteredEntities.push(outputMediaEntity);
      }
    });
    return filteredEntities.sort((a, b) => {
      return a.frameStart - b.frameStart;
    });
  },
);

export const getFullCreativeOutputMediaForEpisode = createSelector(
  [getOutputMediaEntities],
  (outputMediaEntities) => {
    if (isNil(outputMediaEntities)) return undefined;

    for (let i = 0; i < outputMediaEntities.length; i++) {
      const outputMediaEntity = OutputMediaEntityWireObject.fromJSON(
        outputMediaEntities[i],
      );
      if (
        outputMediaEntity.outputMediaType === OUTPUT_MEDIA_TYPES.FULL_CREATIVE
      ) {
        return outputMediaEntity;
      }
    }
    return undefined;
  },
);

export const getHelpUrl = createSelector(
  [inEpisodeFlow, isUserAnAdvertiser, isUserAPublisher],
  (inEpisodeCreator, isAdvertiser, isPublisher) => {
    if (!inEpisodeCreator) return null;
    if (isAdvertiser) return EPISODE_CREATOR_ADVERTISER_HELP_URL;
    if (isPublisher) return EPISODE_CREATOR_PUBLISHER_HELP_URL;

    return null;
  },
);

export const getProductUpdatesUrl = createSelector(
  [inEpisodeFlow, isUserAnAdvertiser, isUserAPublisher],
  (inEpisodeCreator, isAdvertiser, isPublisher) => {
    if (!inEpisodeCreator) return null;
    if (isAdvertiser) return EPISODE_CREATOR_ADVERTISER_PRODUCT_UPDATES_URL;
    if (isPublisher) return EPISODE_CREATOR_PUBLISHER_PRODUCT_UPDATES_URL;

    return null;
  },
);

export const getFilteredMediaList = createSelector(
  [mediaList, isEditingTemplateMode, currentOrganization, getCurrentPublisher],
  (userMediaList, editingTemplateMode, currentOrg, currentPublisher) => {
    if (isEmpty(userMediaList)) return userMediaList;

    // If not in template mode, return the normal mediaList
    if (!editingTemplateMode) return userMediaList;

    // In template mode, filter only to media owned in account to prevent cross-acct ownership issues
    return userMediaList.filter((m) => {
      return (
        (isNotNil(currentOrg) && m.organizationId === currentOrg.id) ||
        (isNotNil(currentPublisher) && m.publisherId === currentPublisher.id)
      );
    });
  },
);
